<template>
<div class="ag-root" :style="themePageStyle">

  <nav class="ag-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <button class="ag-back" @click="goBack" title="Back"><i class="mdi mdi-arrow-left"></i></button>
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Agro-Input Shop</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/sell/dashboard')"><i class="mdi mdi-storefront me-1"></i><span class="ag-cta-lbl">I'm a Seller</span></button>
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/cart')"><i class="mdi mdi-cart me-1"></i><span class="ag-cta-lbl">Cart</span></button>
      <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i><span class="ag-cta-lbl">Animals</span></button>
    </div>
  </div></nav>

  <div class="container ag-container">

    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">📦 Agro-Input Shop</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        Animal feeds, supplements, vet drugs, equipment and supplies — delivered to your farm or pick up from nearby stockists.
      </p>
    </div>

    <!-- Search + price filter -->
    <div class="ag-filter-row">
      <div class="input-group" style="max-width:380px;">
        <span class="input-group-text ag-addon"><i class="mdi mdi-magnify"></i></span>
        <input v-model="filter.keyword" class="form-control ag-input" placeholder="Feeds, dewormers, vaccines..." @input="onKeywordInput" @keyup.enter="loadProducts" />
      </div>
      <input type="number" v-model="filter.priceMin" class="form-control ag-input" placeholder="Min KES" style="max-width:140px;" @change="loadProducts" />
      <input type="number" v-model="filter.priceMax" class="form-control ag-input" placeholder="Max KES" style="max-width:140px;" @change="loadProducts" />
      <button class="btn btn-warning" @click="loadProducts"><i class="mdi mdi-filter-variant"></i> Search</button>
    </div>

    <!-- View toggle: grid / list -->
    <div class="ag-view-row">
      <button class="ag-view-toggle" :class="{active: viewMode==='grid'}" @click="setViewMode('grid')" title="Grid view"><i class="mdi mdi-view-grid"></i> Grid</button>
      <button class="ag-view-toggle" :class="{active: viewMode==='list'}" @click="setViewMode('list')" title="List view"><i class="mdi mdi-view-list"></i> List</button>
    </div>

    <!-- Categories -->
    <div class="d-flex gap-2 mb-4 flex-wrap">
      <button class="ag-chip" :class="{active: !filter.categoryId}" @click="filter.categoryId=''; loadProducts()">All</button>
      <button v-for="c in categories" :key="c.id" class="ag-chip"
              :class="{active: filter.categoryId == c.id}"
              @click="filter.categoryId = filter.categoryId == c.id ? '' : c.id; loadProducts()">
        <i v-if="c.icon" class="mdi" :class="c.icon" style="margin-right:4px;"></i>{{ c.name }}
      </button>
    </div>

    <!-- Loading / empty / grid -->
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!products.length" class="text-center py-5">
      <i class="mdi mdi-package-variant-closed-remove" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No products match</h5>
      <p style="color:var(--fm-s,#6B6356);">Try a different category or broaden your price range.</p>
    </div>
    <div v-else>
      <p style="color:var(--fm-s,#6B6356);font-size:12px;">{{ total }} product{{ total === 1 ? '' : 's' }} found</p>
      <div class="row g-3" :class="'ag-mode-' + viewMode">
        <div :class="viewMode==='list' ? 'col-12' : 'col-md-6 col-lg-4 col-xl-3'" v-for="p in products" :key="p.id">
          <div class="ag-card" @click="$router.push('/shop/product/' + (p.slug || p.id))">
            <div class="ag-cover" :style="p.primary_image_url ? 'background-image:url('+imgUrl(p.primary_image_url)+')' : ''">
              <i v-if="!p.primary_image_url" class="mdi mdi-package-variant" style="font-size:42px;color:var(--fm-s,#3A3530);"></i>
              <span v-if="String(p.is_featured) === '1'" class="ag-feat"><i class="mdi mdi-star me-1"></i>Featured</span>
              <span v-if="Number(p.stock_quantity||0) <= 0 && Number(p.allow_backorder||0) === 0" class="ag-oos">Out of stock</span>
              <span v-else-if="Number(p.stock_quantity||0) > 0 && Number(p.stock_quantity||0) <= Number(p.low_stock_threshold||5)" class="ag-low">Low stock</span>
            </div>
            <div class="ag-body">
              <span v-if="p.category_name" class="ag-cat">{{ p.category_name }}</span>
              <div class="ag-name">{{ p.name }}</div>
              <div v-if="p.brand || p.manufacturer" class="ag-brand">{{ p.brand || p.manufacturer }}</div>
              <div class="ag-price-row">
                <div class="ag-price">
                  KES {{ fNum(p.price) }}
                  <span v-if="p.price_unit" class="ag-unit">/{{ p.price_unit }}</span>
                  <span v-if="p.compare_at_price && Number(p.compare_at_price) > Number(p.price)" class="ag-strike">KES {{ fNum(p.compare_at_price) }}</span>
                </div>
                <button class="ag-add"
                        :class="{added: addedId === p.id, disabled: (Number(p.stock_quantity||0) <= 0 && Number(p.allow_backorder||0) === 0)}"
                        :disabled="addingId === p.id || (Number(p.stock_quantity||0) <= 0 && Number(p.allow_backorder||0) === 0)"
                        @click.stop="addToCart(p)">
                  <span v-if="addingId === p.id" class="spinner-border spinner-border-sm"></span>
                  <i v-else-if="addedId === p.id" class="mdi mdi-cart-check"></i>
                  <i v-else class="mdi mdi-cart-plus"></i>
                  <span class="ag-add-lbl">{{ addedId === p.id ? 'Added' : 'Add' }}</span>
                </button>
              </div>
              <div v-if="p.seller_name" class="ag-seller"><i class="mdi mdi-storefront me-1"></i>{{ p.seller_name }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="total > pageSize" class="d-flex justify-content-center gap-2 mt-4">
      <button class="btn btn-sm btn-outline-warning" :disabled="page <= 1" @click="page--; loadProducts()"><i class="mdi mdi-arrow-left"></i> Prev</button>
      <span style="color:var(--fm-m,#A09888);font-size:13px;align-self:center;">Page {{ page }} of {{ Math.ceil(total / pageSize) }}</span>
      <button class="btn btn-sm btn-outline-warning" :disabled="page * pageSize >= total" @click="page++; loadProducts()">Next <i class="mdi mdi-arrow-right"></i></button>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .ag-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .ag-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ag-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ag-input,
html[data-theme="light"] .ag-addon,
html[data-theme="light"] .ag-chip{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#555!important}
</style>

<style scoped>
.ag-back{width:36px;height:36px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:19px;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}
.ag-back:hover{background:rgba(191,161,100,.15);color:#BFA164}
html[data-theme="light"] .ag-back{background:#F0EBE4!important;color:#1A1A1A!important}
.ag-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ag-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ag-container{padding-top:80px;padding-bottom:60px;max-width:1200px}
.ag-filter-row{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;align-items:stretch}
.ag-input{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.ag-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.ag-addon{background:var(--fm-bg2,#0F0F0F)!important;border-color:var(--fm-br,#1E1E1E)!important;color:var(--fm-s,#6B6356)!important}
.ag-chip{padding:6px 14px;border-radius:20px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.ag-chip:hover{border-color:#BFA164;color:#BFA164}
.ag-chip.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164;font-weight:600}

.ag-view-row{display:flex;justify-content:flex-end;gap:6px;margin-bottom:10px}
.ag-view-toggle{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);border-radius:8px;padding:6px 12px;font-size:12px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:5px}
.ag-view-toggle:hover{border-color:#BFA164;color:#BFA164}
.ag-view-toggle.active{background:rgba(191,161,100,.14);border-color:#BFA164;color:#BFA164}

/* List-mode override: each card becomes a horizontal row.
 * !important needed because the mobile @media block on grid mode is also !important. */
.ag-mode-list .ag-card{flex-direction:row !important;height:auto !important}
.ag-mode-list .ag-cover{flex:0 0 110px !important;width:110px !important;height:110px !important;min-height:110px !important}
.ag-mode-list .ag-body{flex:1 !important;min-width:0 !important;padding:12px !important}
.ag-mode-list .ag-price-row{margin-top:6px !important}
.ag-mode-list .ag-add .ag-add-lbl{display:inline !important}

.ag-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;overflow:hidden;cursor:pointer;transition:all .2s;height:100%;display:flex;flex-direction:column}
.ag-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.ag-cover{height:160px;background:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;background-size:cover;background-position:center;position:relative}
.ag-feat{position:absolute;top:8px;left:8px;background:rgba(255,215,0,.2);color:#FFD700;font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600}
.ag-oos{position:absolute;top:8px;right:8px;background:rgba(255,107,107,.2);color:#FF6B6B;font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600}
.ag-low{position:absolute;top:8px;right:8px;background:rgba(255,152,0,.2);color:#FF9800;font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600}
.ag-body{padding:12px;flex:1;display:flex;flex-direction:column}
.ag-cat{display:inline-block;font-size:10px;color:#BFA164;text-transform:uppercase;letter-spacing:.4px;font-weight:600;margin-bottom:4px}
.ag-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;line-height:1.4;margin-bottom:4px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.ag-brand{color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:6px}
.ag-price-row{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:auto}
.ag-price{color:#BFA164;font-weight:700;font-size:15px;flex:1;min-width:0}
.ag-unit{color:var(--fm-s,#6B6356);font-size:11px;font-weight:400;margin-left:2px}
.ag-strike{color:var(--fm-s,#6B6356);font-size:11px;font-weight:400;text-decoration:line-through;margin-left:8px}
.ag-add{display:inline-flex;align-items:center;gap:4px;padding:6px 12px;border-radius:8px;border:none;background:#BFA164;color:#1A1A1A;font-weight:700;font-size:12px;cursor:pointer;transition:all .15s;flex-shrink:0;white-space:nowrap}
.ag-add:hover:not(:disabled){background:#D4B379;transform:translateY(-1px)}
.ag-add:disabled{cursor:not-allowed}
.ag-add.disabled{background:#3A3530;color:#6B6356}
.ag-add.added{background:#50C878;color:#0A0A0A}
.ag-seller{color:var(--fm-m,#A09888);font-size:10px;margin-top:6px;padding-top:6px;border-top:1px dashed var(--fm-br,#1E1E1E)}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  /* Sticky compact topbar: brand on left, icon-only CTAs on right */
  .ag-topbar { padding: 8px 0 !important; }
  .ag-topbar .container { padding-left: 10px !important; padding-right: 10px !important; }
  .ag-topbar img { height: 28px !important; }
  .ag-topbar .fw-semibold { font-size: 13px !important; }
  .ag-topbar .btn { padding: 6px 10px !important; font-size: 12px !important; }
  /* Collapse CTA labels to icons on tiny widths */
  .ag-topbar .btn .me-1 + span,
  .ag-topbar .btn::after { display: inline; }

  /* Tight container — every pixel counts */
  .ag-container { padding-top: 12px !important; padding-bottom: calc(40px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 10px !important; padding-right: 10px !important; }

  /* Hero shrunk so the catalog is visible above the fold */
  .ag-container > .text-center.mb-4 { margin-bottom: 12px !important; }
  .ag-container > .text-center.mb-4 h4 { font-size: 18px !important; margin-bottom: 4px !important; }
  .ag-container > .text-center.mb-4 p { font-size: 11px !important; line-height: 1.4 !important; }

  /* Stack filter row: search full-width, then Min/Max/Search in one row */
  .ag-filter-row { gap: 6px !important; }
  .ag-filter-row .input-group { flex: 1 1 100% !important; max-width: 100% !important; }
  .ag-filter-row > input[type="number"] { flex: 1 1 0 !important; max-width: none !important; min-width: 0 !important; font-size: 12px !important; }
  .ag-filter-row > .btn { flex: 0 0 auto !important; padding: 6px 12px !important; font-size: 12px !important; white-space: nowrap; }

  /* Category chips: horizontal scroll instead of wrapping */
  .ag-container > .d-flex.gap-2.mb-4.flex-wrap { flex-wrap: nowrap !important; overflow-x: auto; padding-bottom: 4px; margin-bottom: 14px !important; scrollbar-width: none; }
  .ag-container > .d-flex.gap-2.mb-4.flex-wrap::-webkit-scrollbar { display: none; }
  .ag-chip { flex: 0 0 auto !important; white-space: nowrap; padding: 6px 12px !important; font-size: 11px !important; }

  /* 2-column product grid on mobile (grid mode only — list mode keeps col-12 full rows) */
  .ag-mode-grid > [class*="col-"] { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 6px !important; padding-right: 6px !important; }
  .ag-mode-grid { margin-left: -6px !important; margin-right: -6px !important; --bs-gutter-y: 12px !important; }

  /* Card — slim, tap-friendly */
  .ag-card { border-radius: 10px !important; }
  .ag-mode-grid .ag-cover { height: 110px !important; }
  .ag-body { padding: 8px !important; }
  .ag-cat { font-size: 9px !important; margin-bottom: 3px !important; }
  .ag-name { font-size: 12px !important; line-height: 1.3 !important; margin-bottom: 3px !important; }
  .ag-brand { font-size: 10px !important; margin-bottom: 4px !important; }
  .ag-price-row { gap: 4px !important; }
  .ag-price { font-size: 13px !important; }
  .ag-unit { font-size: 9px !important; }
  .ag-strike { font-size: 10px !important; margin-left: 4px !important; }
  .ag-add { padding: 5px 8px !important; font-size: 10px !important; gap: 3px !important; }
  .ag-add .ag-add-lbl { display: none; }
  .ag-seller { font-size: 9px !important; padding-top: 4px !important; margin-top: 4px !important; }
  .ag-feat, .ag-oos, .ag-low { font-size: 9px !important; padding: 1px 6px !important; }

  /* Result count subtle on mobile */
  .ag-container p[style*="font-size:12px"] { font-size: 10px !important; margin-bottom: 8px !important; }
}

/* Very tight phones — collapse topbar CTAs to icons only */
@media (max-width: 480px) {
  .ag-topbar .btn { padding: 6px 10px !important; }
  .ag-topbar .btn .mdi { margin-right: 0 !important; }
  .ag-topbar .ag-cta-lbl { display: none !important; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "AgroInputShop",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:false,
    filter:{ categoryId:'', keyword:'', priceMin:'', priceMax:'' },
    categories:[], products:[], total:0, page:1, pageSize:24,
    _searchTimer:null, addingId:'', addedId:'',
    viewMode: (localStorage.getItem('flockr_shop_view') || 'grid')
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'}
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadCategories();
    this.loadProducts();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    fNum(n){return Number(n||0).toLocaleString()},
    imgUrl(u){if(!u)return'';if(u.indexOf('http')===0)return u;return uploads+String(u).replace(/^uploads\//,'');},
    async loadCategories(){
      try{var r=await executeJson({function:'getProductCategories'},flocker);
        if(r.data&&r.data.success){
          var d=r.data.data;
          this.categories=(d&&d.categories)||(Array.isArray(d)?d:[]);
        }
      }catch(e){}
    },
    async loadProducts(){
      this.loading=true;
      try{
        var params={function:'searchProducts',page:this.page,limit:this.pageSize};
        if(this.filter.categoryId) params.categoryId=this.filter.categoryId;
        if(this.filter.keyword) params.keyword=this.filter.keyword;
        if(this.filter.priceMin) params.priceMin=this.filter.priceMin;
        if(this.filter.priceMax) params.priceMax=this.filter.priceMax;
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success){
          this.products=(r.data.data&&r.data.data.products)||[];
          this.total=Number(r.data.data&&r.data.data.total||0);
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    onKeywordInput(){
      // Debounce live search so we don't fire on every keystroke.
      if(this._searchTimer) clearTimeout(this._searchTimer);
      var self=this;
      this._searchTimer=setTimeout(function(){
        self.page=1;
        self.loadProducts();
      },300);
    },
    setViewMode(m){
      this.viewMode = m;
      localStorage.setItem('flockr_shop_view', m);
    },
    goBack(){ if(window.history.length>1) this.$router.go(-1); else this.$router.push('/'); },
    async addToCart(p){
      if(!p || !p.id) return;
      // Block obviously out-of-stock items so we don't fire pointless API calls.
      var stock=Number(p.stock_quantity||0);
      var backorder=Number(p.allow_backorder||0);
      if(stock<=0 && backorder===0) return;
      var userId=sessionStorage.getItem('userId');
      if(!userId){ this.$router.push('/login'); return; }
      this.addingId=p.id;
      try{
        var r=await executeJson({function:'addToCart',userId:userId,productId:p.id,quantity:1},flocker);
        if(r.data&&r.data.success){
          this.addedId=p.id;
          var self=this;
          setTimeout(function(){ if(self.addedId===p.id) self.addedId=''; },1500);
        }
      }catch(e){}
      this.addingId='';
    }
  }
};
</script>
